// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SectionContainer, SectionTitle } from "@sc/App/Widgets/DesignSystem/DesignSystem.helpers";
import typography from "@sc/XDL/PreStyle/typography";
import React, { FC } from "react";
import { css } from "styled-components";

interface TypographyTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
}

const typogrphyTabStyles: TypographyTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
            flex-wrap: wrap;
        `,
    },
};

const styles = typogrphyTabStyles;
const TypographyTab: FC = props => {
    return (
        <SectionContainer>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Display Titles : For Responsiveness")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Typography
                    size={36}
                    lineHeight="49px"
                    weight={800}
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    {translate(`Desktop / Tablet Display – [Arimo] : [800] : Font-Size [36]px : Line-Height [49]px`)}
                </Typography>
                <Typography size={28} lineHeight="38px" weight={800}>
                    {translate(`Mobile Display – [Arimo] : [800] : Font-Size [28]px : Line-Height [38]px`)}
                </Typography>
            </GridItem>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Hierarchy")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Typography variant="h1">
                    {translate(
                        `H1 – [Font] : [${typography["h1"].weight}] : Font-Size [${typography["h1"].size}] : Line-Height [${typography["h1"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h2">
                    {translate(
                        `H2 – [Font] : [${typography["h2"].weight}] : Font-Size [${typography["h2"].size}] : Line-Height [${typography["h2"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h3">
                    {translate(
                        `H3 – [Font] : [${typography["h3"].weight}] : Font-Size [${typography["h3"].size}] : Line-Height [${typography["h3"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h4">
                    {translate(
                        `H4 – [Font] : [${typography["h4"].weight}] : Font-Size [${typography["h4"].size}] : Line-Height [${typography["h4"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h5">
                    {translate(
                        `H5 – [Font] : [${typography["h5"].weight}] : Font-Size [${typography["h5"].size}] : Line-Height [${typography["h5"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h6">
                    {translate(
                        `H6 – [Font] : [${typography["h6"].weight}] : Font-Size [${typography["h6"].size}] : Line-Height [${typography["h6"].lineHeight}]`,
                    )}
                </Typography>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Body Copy : Regular</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Typography variant="p">Roboto : 400 : Font-Size 16px : Line-Height: 26px</Typography>
                    <Typography variant="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat. Lorem ipsum dolor sit amet, consectetur
                        adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad
                        minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Typography variant="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat. Lorem ipsum dolor sit amet, consectetur
                        adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad
                        minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Links</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Link href="https://www.google.com/">Google</Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Ordered List</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList>
                        <ListItem>
                            Item 1
                            <OrderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Unordered List</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList>
                        <ListItem>
                            Item 1
                            <UnorderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default TypographyTab;
